import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/services/siteSettings";

const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Projects", to: "/projects" },
  { label: "Destinations", to: "/destinations" },
  { label: "Villa Models", to: "/listings" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Investment Guide", to: "/investment-guide" },
  { label: "Returns", to: "/returns" },
  { label: "Contact", to: "/contact" },
];

export default function PublicNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { get: getSetting } = useSiteSettings();
  const brandLogo = getSetting("brand_logo_url", "");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const solid = scrolled || open;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2.5 whitespace-nowrap">
            {brandLogo ? (
              <img src={brandLogo} alt="Nomad Livings logo" className="h-9 w-auto rounded-lg object-contain" />
            ) : (
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-primary-600 text-background-50">
                <i className="ri-home-4-line text-lg"></i>
              </span>
            )}
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading font-semibold text-lg tracking-tight ${
                  solid ? "text-foreground-950" : "text-background-50"
                }`}
              >
                Nomad <span className="text-accent-500">Livings</span>
              </span>
              <span
                className={`text-[10px] tracking-[0.28em] uppercase ${
                  solid ? "text-foreground-600" : "text-background-100/90"
                }`}
              >
                A-Frame Villa Investment
              </span>
            </span>
          </Link>

          <ul className="hidden lg:flex items-center gap-6">
            {NAV_LINKS.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className={`text-sm font-medium whitespace-nowrap transition-colors ${
                    solid
                      ? pathname === link.to
                        ? "text-primary-700"
                        : "text-foreground-700 hover:text-primary-600"
                      : "text-background-50/90 hover:text-background-50"
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <a
              href="tel:8088330330"
              className={`hidden md:flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full px-4 py-2 border transition-colors ${
                solid
                  ? "border-primary-600 text-primary-700 hover:bg-primary-600 hover:text-background-50"
                  : "border-background-50/60 text-background-50 hover:bg-background-50 hover:text-primary-800"
              }`}
            >
              <i className="ri-phone-fill"></i>
              80883 30330
            </a>
            <Link
              to="/contact"
              className="hidden md:inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-accent-500 text-background-50 px-5 py-2 hover:bg-accent-600 transition-colors"
            >
              Schedule a Visit
              <i className="ri-arrow-right-line"></i>
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              className="lg:hidden flex items-center justify-center w-10 h-10 rounded-full border border-background-200/60 text-foreground-950 bg-background-50/80"
            >
              <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden pb-5 max-h-[70vh] overflow-y-auto">
            <ul className="flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li className="pt-2 flex flex-col gap-2">
                <a
                  href="tel:8088330330"
                  className="flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-primary-600 text-primary-700 px-4 py-2.5"
                >
                  <i className="ri-phone-fill"></i> Call / WhatsApp
                </a>
                <Link
                  to="/contact"
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-accent-500 text-background-50 px-4 py-2.5"
                >
                  Schedule a Visit
                </Link>
              </li>
            </ul>
          </div>
        )}
      </nav>
    </header>
  );
}